import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronRight, Clock, CheckCircle2, XCircle, AlertCircle, FileText, Search, ShoppingBag, Loader2, ReceiptEuro } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { fetchMyRMAs } from '../../services/rmaService';
import { LoadError } from '../../components/rma/LoadError';
import { useRMACart } from '../../context/RMACartContext';
import { formatErpRmaNumber } from '../../types';
import type { RMARequest } from '../../types';
import {
  Popover as PopoverRoot,
  PopoverTrigger as PopoverTriggerRoot,
  PopoverContent as PopoverContentRoot,
} from '../../components/ui/popover';
import { documentiOrigine, hrefDocumento } from '../../components/rma/documentiRma';

const RMA_STATUS: Record<string, { label: string; color: string; icon: any }> = {
  aperta:         { label: 'Aperta',                  color: 'bg-blue-100 text-blue-700',    icon: Clock },
  in_lavorazione: { label: 'In Lavorazione',          color: 'bg-amber-100 text-amber-700',  icon: AlertCircle },
  chiusa:         { label: 'Chiusa',                  color: 'bg-gray-100 text-gray-600',    icon: CheckCircle2 },
};

const Popover = PopoverRoot as React.FC<{ children?: React.ReactNode }>;
const PopoverTrigger = PopoverTriggerRoot as React.FC<{ asChild?: boolean; children?: React.ReactNode }>;
const PopoverContent = PopoverContentRoot as React.FC<{ className?: string; align?: 'start' | 'center' | 'end'; children?: React.ReactNode }>;

const ICONA = 'h-7 rounded-lg flex items-center justify-center gap-1 bg-[hsl(214,58%,41%)]/10 text-[hsl(214,58%,41%)] hover:bg-[hsl(214,58%,41%)]/20 transition-colors';

interface Voce {
  key: string;
  label: string;
  href: string;
}

/** Cella di link di una riga: con una voce l'icona la apre, con più voci
 *  mostra quante sono e apre un elenco. I click non arrivano alla riga
 *  (che apre il dettaglio), nemmeno dall'elenco, che React fa risalire
 *  anche dal portale del popover. */
function CellaLink({ voci, Icon, titolo, testid }: { voci: Voce[]; Icon: LucideIcon; titolo: [string, string]; testid: string }) {
  const ferma = (e: React.SyntheticEvent) => e.stopPropagation();
  if (voci.length === 1) {
    const v = voci[0];
    return (
      <Link to={v.href} onClick={ferma} title={`${titolo[0]} ${v.label}`} data-testid={testid} className={`${ICONA} w-7`}>
        <Icon className="w-3.5 h-3.5" />
      </Link>
    );
  }
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" onClick={ferma} onKeyDown={ferma} title={`${voci.length} ${titolo[1]}`} data-testid={testid} className={`${ICONA} px-1.5`}>
          <Icon className="w-3.5 h-3.5" />
          <span className="text-[10px] font-semibold">{voci.length}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="center" className="w-auto min-w-[10rem] p-1">
        <div onClick={ferma} onKeyDown={ferma}>
        {voci.map(v => (
          <Link key={v.key} to={v.href} className="block px-2.5 py-1.5 rounded text-xs font-mono text-[hsl(214,58%,41%)] hover:bg-secondary">
            {v.label}
          </Link>
        ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function RMAPage() {
  const { totalUnits, totalLines } = useRMACart();
  const navigate = useNavigate();
  const [rmas, setRmas] = useState<RMARequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    fetchMyRMAs()
      .then(items => { if (!cancelled) setRmas(items); })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [attempt]);

  const allRmas = rmas;
  return (
    <div data-testid="rma-page">
      <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">RMA / Resi</h1>
            <p className="text-muted-foreground text-sm mt-1">Gestisci le pratiche di reso e monitora lo stato.</p>
          </div>
        </div>

        {/* Carrello resi banner (se ci sono articoli in preparazione) */}
        {totalUnits > 0 && (
          <Link
            to="/area-clienti/rma/preparazione"
            className="block mb-4 rounded-xl border-2 border-emerald-300 bg-emerald-50 p-4 hover:bg-emerald-100/60 transition-colors"
            data-testid="rma-cart-banner"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-600 text-white flex items-center justify-center flex-shrink-0">
                <ShoppingBag className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <div className="text-sm font-semibold text-emerald-900">Hai una pratica RMA in preparazione</div>
                <div className="text-xs text-emerald-800">{totalUnits} {totalUnits === 1 ? 'pezzo' : 'pezzi'} · {totalLines} {totalLines === 1 ? 'articolo' : 'articoli'} · clicca per riprendere</div>
              </div>
              <ChevronRight className="w-5 h-5 text-emerald-700" />
            </div>
          </Link>
        )}

        {/* Doppio accesso: da documento + da ricerca prodotto */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <Link
            to="/area-clienti/rma/da-documento"
            className="rounded-xl border bg-white p-5 hover:border-[hsl(var(--dtk-focus))] hover:bg-[hsl(var(--dtk-canvas))]/40 transition-all group"
            data-testid="rma-from-document"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[hsl(214,58%,41%)]/10 flex items-center justify-center flex-shrink-0">
                <FileText className="w-5 h-5 text-[hsl(214,58%,41%)]" />
              </div>
              <div className="flex-1">
                <div className="text-sm font-semibold group-hover:text-[hsl(214,58%,41%)] transition-colors">Reso da Documenti</div>
                <div className="text-xs text-muted-foreground">Seleziona la fattura ricevuta e scegli i prodotti da rendere</div>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" />
            </div>
          </Link>
          <Link
            to="/area-clienti/rma/cerca"
            className="rounded-xl border bg-white p-5 hover:border-[hsl(var(--dtk-focus))] hover:bg-[hsl(var(--dtk-canvas))]/40 transition-all group"
            data-testid="rma-from-search"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[hsl(214,58%,41%)]/10 flex items-center justify-center flex-shrink-0">
                <Search className="w-5 h-5 text-[hsl(214,58%,41%)]" />
              </div>
              <div className="flex-1">
                <div className="text-sm font-semibold group-hover:text-[hsl(214,58%,41%)] transition-colors">Cerca prodotto</div>
                <div className="text-xs text-muted-foreground">Cerca per codice o nome e trova il prodotto da rendere</div>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" />
            </div>
          </Link>
        </div>

        {/* RMA List — vista tabellare compatta */}
        {loading ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground" data-testid="rma-list-loading">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Caricamento pratiche...
          </div>
        ) : error ? (
          <LoadError message="Non è stato possibile caricare le pratiche di reso." onRetry={() => setAttempt(a => a + 1)} testId="rma-list-error" />
        ) : allRmas.length === 0 ? (
          <div className="rounded-xl border bg-white p-8 text-center text-sm text-muted-foreground" data-testid="rma-list-empty">
            Non hai ancora aperto nessuna pratica di reso.
          </div>
        ) : (
        <div className="rounded-xl border bg-white overflow-hidden shadow-sm" data-testid="rma-table">
          {/* Header colonne */}
          <div className="hidden md:grid grid-cols-[9rem_7rem_minmax(10rem,1fr)_6rem_7rem_2.5rem] bg-[hsl(210,64%,29%)] text-white text-[11px] font-semibold uppercase tracking-wide">
            <div className="px-3 py-2.5">Numero RMA</div>
            <div className="px-3 py-2.5">Data</div>
            <div className="px-3 py-2.5">Stato</div>
            <div className="px-3 py-2.5 text-center">Documenti</div>
            <div className="px-3 py-2.5 text-center">Nota credito</div>
            <div className="px-2 py-2.5" />
          </div>

          {allRmas.map((rma: any, idx: number) => {
            const st = RMA_STATUS[rma.status];
            const StIcon = st?.icon || Clock;
            // Un RMA può mettere insieme pezzi di più Documenti.
            const documenti: Voce[] = documentiOrigine(rma.items).map(d => ({
              key: d.numero, label: d.numero, href: hrefDocumento(d),
            }));
            const note: Voce[] = (rma.noteCredito ?? []).map((n: any) => ({
              key: n.numero, label: n.numero, href: hrefDocumento(n),
            }));
            return (
              <div
                key={rma.id}
                data-testid={`rma-row-${rma.id}`}
                onClick={() => navigate(`/area-clienti/rma/${rma.id}`)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/area-clienti/rma/${rma.id}`); }}
                className={`w-full grid grid-cols-2 md:grid-cols-[9rem_7rem_minmax(10rem,1fr)_6rem_7rem_2.5rem] items-center gap-1 py-2.5 px-3 md:px-0 text-left border-b last:border-b-0 cursor-pointer hover:bg-[hsl(210,40%,97%)] transition-colors ${idx % 2 === 1 ? 'bg-[hsl(210,40%,99%)]' : 'bg-white'}`}
              >
                <div className="px-0 md:px-3 font-mono text-sm font-bold" data-testid={`rma-numero-${rma.id}`}>
                  {rma.id}
                  {formatErpRmaNumber(rma.erpRma) && (
                    <span className="block text-[10px] font-normal text-muted-foreground" data-testid={`rma-erp-numero-${rma.id}`}>
                      {formatErpRmaNumber(rma.erpRma)}
                    </span>
                  )}
                </div>
                <div className="px-0 md:px-3 text-sm text-muted-foreground" data-testid={`rma-data-${rma.id}`}>{rma.date}</div>
                <div className="px-0 md:px-3 col-span-2 md:col-span-1">
                  <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded font-medium ${st?.color}`} data-testid={`rma-stato-${rma.id}`}>
                    <StIcon className="w-3 h-3" /> {st?.label}
                  </span>
                </div>
                <div className="px-0 md:px-3 flex md:justify-center">
                  {documenti.length > 0 ? (
                    <CellaLink voci={documenti} Icon={FileText} titolo={['Documento', 'documenti']} testid={`rma-documento-${rma.id}`} />
                  ) : (
                    <span
                      title="Documento non disponibile per questa pratica"
                      data-testid={`rma-documento-${rma.id}`}
                      className="w-7 h-7 rounded-lg flex items-center justify-center bg-secondary/60 text-muted-foreground/50 cursor-not-allowed"
                    >
                      <FileText className="w-3.5 h-3.5" />
                    </span>
                  )}
                </div>
                <div className="px-0 md:px-3 flex md:justify-center">
                  {note.length > 0 && (
                    <CellaLink voci={note} Icon={ReceiptEuro} titolo={['Nota di credito', 'note di credito']} testid={`rma-nota-credito-${rma.id}`} />
                  )}
                </div>
                <div className="px-2 hidden md:flex justify-center">
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                </div>
              </div>
            );
          })}
        </div>
        )}
    </div>
  );
}
